import { SectionElementProps, ElementType, SectionProps } from '../components/Section/Section';
import { contentfulApiContentBannerToContentBannerProps } from './contentfulApiContentBannerToContentBannerProps';
import { contentfulApiCardGridToCardGridWithProviderProps } from './contentfulApiCardGridToCardGridWithProviderProps';
import {
  ContentfulCardGrid,
  ContentfulCarousel,
  ContentfulContentBanner,
  ContentfulHeroBlock,
  ContentfulSection,
  ContentfulSubCategoryPage,
  ContentfulCarouselType,
} from '@jbhi-fi/apis/src/contentful/types';
import { HeroBlockTransformer } from '@jbhi-fi/components/src/Contentful/transformers/HeroBlockTransformer';
import { ContentBlockStyleProps } from '@jbhi-fi/components/src/ContentBlock';
import { HeadingDividerVariant } from '@jbhi-fi/components/src/HeadingDivider/HeadingDivider';
import { determineCarouselTransformer } from './determineCarouselTransformer';

const sectionHasElementType = (section: ContentfulSection, elementType: string) =>
  section?.fields?.sectionContent?.findIndex(element => element?.sys?.contentType?.sys?.id === elementType) !== -1;

export type ContentfulApiSectionToSectionPropsOptions = {
  contentBlockProps?: ContentBlockStyleProps;
};

export const contentfulApiSectionToSectionProps = (
  section: ContentfulSection,
  options: ContentfulApiSectionToSectionPropsOptions = {}
): SectionProps | undefined => {
  const sectionElements = section?.fields?.sectionContent
    ?.map(element => getSectionElementProps({ element, section }))
    ?.filter(Boolean) as Array<SectionElementProps<any>>;
  const baseSectionProps = getSectionPropsFromElementType(section);

  return section?.fields && sectionElements?.length
    ? {
        href: section?.fields?.url,
        shouldOpenInNewWindow: section?.fields?.shouldOpenInNewWindow,
        title: section.fields.hideHeading ? undefined : section.fields.heading,
        ...baseSectionProps,
        contentBlockProps: {
          ...(baseSectionProps.contentBlockProps ?? {}),
          ...(options.contentBlockProps ?? {}),
        },
        elements: sectionElements,
      }
    : undefined;
};

const getSectionPropsFromElementType = (section: ContentfulSection): Omit<SectionProps, 'elements'> => {
  if (sectionHasElementType(section, 'carousel'))
    return getCarouselSectionProps(
      section?.fields?.sectionContent?.find(s => s.sys.contentType?.sys.id === 'carousel') as ContentfulCarousel
    );
  if (sectionHasElementType(section, 'cardGrid'))
    return {
      href: getCardGridParentPageHref(
        section?.fields?.sectionContent?.find(s => s.sys.contentType?.sys.id === 'cardGrid') as ContentfulCardGrid
      ),
    };
  return {};
};

const getCarouselSectionProps = (carousel: ContentfulCarousel): Omit<SectionProps, 'elements'> => {
  switch (carousel.fields?.type) {
    case ContentfulCarouselType.Chart:
    case ContentfulCarouselType.Merch:
    case ContentfulCarouselType.Video:
      return {
        contentBlockProps: {
          paddingLeft: { mobile: 0, tablet: 'xlarge' },
          paddingRight: { mobile: 0, tablet: 'xlarge' },
        },
      };
    default:
      return {
        titleVariant: HeadingDividerVariant.small,
        titlePadding: 5,
      };
  }
};

const getSectionElementProps = ({
  element,
  section,
}: {
  element: ContentfulCardGrid | ContentfulCarousel | ContentfulHeroBlock | ContentfulContentBanner;
  section: ContentfulSection;
}): SectionElementProps<any> | undefined => {
  switch (element.sys?.contentType?.sys?.id) {
    case 'carousel':
      return contentfulApiCarouselToSectionElementProps(element as ContentfulCarousel);
    case 'heroBlock':
      return contentfulApiHeroBlockToSectionElementProps(element as ContentfulHeroBlock);
    case 'contentBanner':
      return contentfulApiContentBannerToSectionElementProps(element as ContentfulContentBanner);
    case 'cardGrid':
      return contentfulApiCardGridToSectionElementProps({ cardGrid: element as ContentfulCardGrid, section });
    default:
      return undefined;
  }
};

const contentfulApiCarouselToSectionElementProps = (
  carousel: ContentfulCarousel
): SectionElementProps<any> | undefined => {
  if (!carousel?.fields) return undefined;
  const props = determineCarouselTransformer(carousel).transformer?.(carousel);
  switch (carousel.fields?.type) {
    case ContentfulCarouselType.Video:
      return { type: ElementType.presentationVideoCarousel, props };
    case ContentfulCarouselType.Merch:
    case ContentfulCarouselType.Chart:
      return { type: ElementType.presentationProductCarousel, props };
    case ContentfulCarouselType.MiniProduct:
    case ContentfulCarouselType.Product:
      return {
        type: ElementType.productCarousel,
        props: {
          ...props,
          heading: undefined,
          ctaButton: undefined,
        },
      };
    default:
      return undefined;
  }
};

const contentfulApiHeroBlockToSectionElementProps = (
  heroBlock: ContentfulHeroBlock
): SectionElementProps<any> | undefined => {
  if (!heroBlock?.fields) return undefined;
  return {
    type: ElementType.heroBlock,
    props: HeroBlockTransformer.toHeroBlockProps(heroBlock, { showTags: false }),
  };
};

const contentfulApiContentBannerToSectionElementProps = (
  contentBanner: ContentfulContentBanner
): SectionElementProps<any> | undefined => {
  if (!contentBanner?.fields) return undefined;
  return {
    type: ElementType.contentBanner,
    props: contentfulApiContentBannerToContentBannerProps(contentBanner, {}),
  };
};

const getCardGridParentPageHref = (cardGrid: ContentfulCardGrid): string | undefined => {
  return cardGrid?.fields
    ? cardGrid?.fields?.parentPage?.sys?.contentType?.sys?.id === 'subLandingPage'
      ? `/blogs/${
          (cardGrid?.fields?.parentPage as ContentfulSubCategoryPage)?.fields?.categoryPage?.fields?.urlSlug ?? ''
        }/${cardGrid?.fields?.parentPage?.fields?.urlSlug}`
      : `/blogs/${cardGrid?.fields?.parentPage?.fields?.urlSlug}`
    : undefined;
};

const contentfulApiCardGridToSectionElementProps = ({
  cardGrid,
  section,
}: {
  cardGrid: ContentfulCardGrid;
  section: ContentfulSection;
}): SectionElementProps<any> | undefined => {
  if (!cardGrid?.fields) return undefined;
  return {
    type: ElementType.cardProvider,
    props: contentfulApiCardGridToCardGridWithProviderProps(cardGrid, { featuredBannerCardIndex: 5, section }),
  };
};
